import { Link } from "react-router-dom";
import { ArrowRight, Target, FileText, Lightbulb, Compass } from "@phosphor-icons/react";
import cgiLogo from "@/assets/cgi-logo.png";

const steps = [
  { Icon: FileText, t: "Frame the problem", d: "Describe your challenge in plain words. The Room represents it faithfully — facts, assumptions, and blind spots." },
  { Icon: Target, t: "Map the problem system", d: "See the categories of interlocking problems and the tensions between them — not one issue, but the whole system." },
  { Icon: Lightbulb, t: "Explore systemic solutions", d: "Explore a systemic solution to your systemic problem — and if it doesn't fit, generate more." },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-foreground text-background">
      <header className="flex items-center justify-between px-8 py-5">
        <div className="flex items-center gap-4">
          <a
            href="https://www.commongroundinstitute.net"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="landing-cgi-logo-link"
            title="Common Ground Institute"
            className="shrink-0 rounded-sm bg-background px-2 py-1.5 transition-opacity hover:opacity-90"
          >
            <img src={cgiLogo} width="1200" height="415" alt="Common Ground Institute" className="h-7 w-auto" />
          </a>
          <span className="hidden h-6 w-px bg-background/25 sm:block" />
          <span className="font-serif text-xl tracking-tight">Executive Strategy Room</span>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/login" data-testid="landing-signin-btn" className="rounded-sm border border-background/30 px-4 py-2 text-xs font-medium transition-colors hover:bg-background/10">Sign in</Link>
          <Link to="/login?mode=register" data-testid="landing-getstarted-btn" className="inline-flex items-center gap-1.5 rounded-sm bg-background px-4 py-2 text-xs font-medium text-foreground transition-colors hover:bg-background/90">Get started <ArrowRight size={14} weight="bold" /></Link>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-8 pb-24 pt-16 sm:pt-24">
        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-background/50">Systemic problem-solving for leaders</p>
        <h1 className="mt-4 max-w-3xl font-serif text-4xl leading-[1.05] sm:text-6xl">Managing complexity, made visible.</h1>
        <p className="mt-6 max-w-2xl text-lg leading-relaxed text-background/70">
          A hard challenge is never one problem — it's a system of interlocking problems that pull on each other. The Executive Strategy Room maps that system and composes a systemic solution that resolves several at once.
        </p>
        <div className="mt-9 flex flex-wrap gap-3">
          <Link to="/login?mode=register" data-testid="landing-hero-cta" className="inline-flex items-center gap-2 rounded-sm bg-background px-6 py-3 text-sm font-medium text-foreground transition-colors hover:bg-background/90">Start a challenge <ArrowRight size={16} weight="bold" /></Link>
          <Link to="/demo" data-testid="landing-howitworks-btn" className="inline-flex items-center gap-2 rounded-sm border border-background/30 px-6 py-3 text-sm font-medium transition-colors hover:bg-background/10"><Compass size={16} /> How it works — see a sample</Link>
          <Link to="/login" className="inline-flex items-center rounded-sm border border-background/30 px-6 py-3 text-sm font-medium transition-colors hover:bg-background/10">I have an account</Link>
        </div>

        <div className="mt-20 grid gap-8 border-t border-background/15 pt-12 sm:grid-cols-3">
          {steps.map(({ Icon, t, d }, i) => (
            <div key={i}>
              <div className="flex items-center gap-2 text-background/60"><Icon size={18} /><span className="font-mono text-[11px]">{String(i + 1).padStart(2, "0")}</span></div>
              <h3 className="mt-3 font-serif text-xl">{t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-background/60">{d}</p>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
